import { FileText, Loader2, Folder, FolderOpen, ChevronRight, ArrowLeft, Home, Search, X } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { ApiService } from '../../services/api';
import { FileFolder } from '../../types/file';
import Modal from '../ui/Modal';

interface PdfFile {
  id: string;
  originalName: string;
  url: string;
  size: number;
  createdAt: string;
  status: string;
  mimeType?: string;
  pages?: Array<{
    id: string;
    pageNumber: number;
    originalImageUrl: string;
  }>;
}

interface PdfSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (file: PdfFile) => void;
  container?: HTMLElement | null;
}

interface BreadcrumbItem {
  id: string;
  name: string;
  type: 'root' | 'category' | 'folder';
}

export default function PdfSelectModal({ isOpen, onClose, onSelect, container }: PdfSelectModalProps) {
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<PdfFile | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: 'root', name: 'Ana Dizin', type: 'root' },
  ]);

  const [categories, setCategories] = useState<FileFolder[]>([]);
  const [rootFolders, setRootFolders] = useState<FileFolder[]>([]);
  const [unassignedFiles, setUnassignedFiles] = useState<PdfFile[]>([]);

  const [currentFolders, setCurrentFolders] = useState<FileFolder[]>([]);
  const [currentFiles, setCurrentFiles] = useState<PdfFile[]>([]);

  useEffect(() => {
    if (isOpen) {
      navigateToRoot();
    } else {
      setBreadcrumbs([{ id: 'root', name: 'Ana Dizin', type: 'root' }]);
      setSearchQuery('');
      setSelectedFile(null);
    };
  }, [isOpen]);

  const navigateToRoot = async () => {
    setLoading(true);

    try {
      const response = await ApiService.listCategories();

      setCategories(response.categories || []);
      setRootFolders(response.rootFolders || []);

      const files = (response.unassignedFiles || []).filter(
        (f: any) => f.mimeType === "application/pdf"
      );

      setUnassignedFiles(files);
      setCurrentFolders([
        ...(response.categories || []).map((c: FileFolder) => ({ ...c, _isCategory: true })),
        ...(response.rootFolders || []),
      ] as any);

      setCurrentFiles(files);
      setBreadcrumbs([{ id: "root", name: "Ana Dizin", type: "root" }]);
    } catch (error) {
      console.error("Dosyalar yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };

  const navigateToCategory = async (category: FileFolder) => {
    setLoading(true);

    try {
      const categoryData = await ApiService.getCategory(category.id);

      const folders = categoryData.folders || [];
      const files = (categoryData.files || []).filter(
        (f: any) => f.mimeType === 'application/pdf'
      );

      setCurrentFolders(folders);
      setCurrentFiles(files);

      setBreadcrumbs([
        { id: 'root', name: 'Ana Dizin', type: 'root' },
        { id: category.id, name: category.name, type: 'category' },
      ]);
    } catch (error) {
      console.error('Kategori yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = async (folder: FileFolder) => {
    setLoading(true);
    try {
      const folderData = await ApiService.getFolder(folder.id);

      const subFolders = folderData.subFolders || [];
      const files = (folderData.files || []).filter(
        (f: any) => f.mimeType === 'application/pdf'
      );

      setCurrentFolders(subFolders);
      setCurrentFiles(files);

      setBreadcrumbs((prev) => [
        ...prev,
        { id: folder.id, name: folder.name, type: 'folder' },
      ]);
    } catch (error) {
      console.error('Klasör yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateToBreadcrumb = async (index: number) => {
    const target = breadcrumbs[index];

    if (target.type === 'root') {
      await navigateToRoot();
    } else if (target.type === 'category') {
      setBreadcrumbs(breadcrumbs.slice(0, index + 1));

      await navigateToCategory({ id: target.id, name: target.name } as FileFolder);
    } else if (target.type === 'folder') {
      setBreadcrumbs(breadcrumbs.slice(0, index + 1));

      await navigateToFolder({ id: target.id, name: target.name } as FileFolder);
    };
  };

  const handleGoBack = () => {
    if (breadcrumbs.length <= 1) return;

    navigateToBreadcrumb(breadcrumbs.length - 2);
  };

  const handleSelect = async (file: PdfFile) => {
    setSelectedFile(file);
    setLoadingDetails(true);

    try {
      const fileDetails = await ApiService.getFile(file.id);

      await onSelect(fileDetails as PdfFile);
      onClose();
    } catch (error: any) {
      alert(error.message || "Dosya detayları alınırken hata oluştu");
    } finally {
      setLoadingDetails(false);
      setSelectedFile(null);
    }
  };

  const handleFolderClick = (folder: any) => {
    if (folder._isCategory || folder.type === "category") navigateToCategory(folder);
    else navigateToFolder(folder);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const filteredFolders = searchQuery.trim() ? currentFolders.filter((f) => f.name.toLowerCase().includes(searchQuery.toLowerCase())) : currentFolders;
  const filteredFiles = searchQuery.trim() ? currentFiles.filter((f: any) => (f.originalName || f.name || '').toLowerCase().includes(searchQuery.toLowerCase())) : currentFiles;

  const isAtRoot = breadcrumbs.length <= 1;
  const hasContent = filteredFolders.length > 0 || filteredFiles.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="lg" showCloseButton={false} container={container}>
      <div className="space-y-0 -m-4 sm:-m-5">

        <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">PDF Seç</h2>
                <p className="text-xs text-gray-500">Dosyalar bölümünden bir PDF seçin</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-5 pb-3 flex items-center gap-3">
            {!isAtRoot && (
              <button
                onClick={handleGoBack}
                className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <ArrowLeft className="w-4 h-4 text-gray-600" />
              </button>
            )}

            <div className="flex items-center gap-1 flex-1 min-w-0 text-sm overflow-x-auto">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.id}>
                  {index > 0 && (
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  )}
                  <button
                    onClick={() => navigateToBreadcrumb(index)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm whitespace-nowrap transition-colors ${index === breadcrumbs.length - 1
                      ? "text-indigo-600 font-medium bg-indigo-50"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                      }`}
                  >
                    {crumb.type === "root" && <Home className="w-3.5 h-3.5" />}
                    {crumb.type !== "root" && <Folder className="w-3.5 h-3.5" />}
                    <span className="max-w-[120px] truncate">{crumb.name}</span>
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="px-5 pb-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Dosya veya klasör ara..."
                className="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder-gray-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-7 h-7 animate-spin text-indigo-600" />
              <span className="ml-3 text-gray-500 text-sm">Yükleniyor...</span>
            </div>
          ) : !hasContent ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">
                {searchQuery ? "Sonuç bulunamadı" : "Bu konumda dosya yok"}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {searchQuery
                  ? "Farklı bir arama terimi deneyin"
                  : "Dosyalar modülünden PDF yükleyebilirsiniz"}
              </p>
            </div>
          ) : (
            <div className="space-y-2">

              {filteredFolders.length > 0 && (
                <div className="space-y-1">
                  {filteredFolders.length > 0 && filteredFiles.length > 0 && (
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider px-1 mb-2">
                      {isAtRoot ? "Kategoriler & Klasörler" : "Klasörler"}
                    </p>
                  )}
                  {filteredFolders.map((folder: any) => {
                    const isCategory = folder._isCategory || folder.type === "category";
                    const folderColor = folder.color || "#6366F1";
                    const subCount = folder.subFolders?.length || folder.folders?.length || 0;
                    const fileCount = folder.files?.length || 0;

                    return (
                      <button
                        key={folder.id}
                        onClick={() => handleFolderClick(folder)}
                        className="w-full text-left group"
                      >
                        <div
                          className="flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 hover:-translate-y-0.5"
                          style={{
                            background: `linear-gradient(135deg, ${folderColor}08, ${folderColor}04)`,
                            border: `1px solid ${folderColor}20`,
                          }}
                        >
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{
                              background: `linear-gradient(135deg, ${folderColor}20, ${folderColor}10)`,
                            }}
                          >
                            {isCategory ? (
                              <FolderOpen className="w-5 h-5" style={{ color: folderColor }} />
                            ) : (
                              <Folder className="w-5 h-5" style={{ color: folderColor }} />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">
                              {folder.name}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {isCategory && (
                                <span className="text-[11px] px-1.5 py-0.5 rounded font-medium"
                                  style={{ background: `${folderColor}15`, color: folderColor }}
                                >
                                  Kategori
                                </span>
                              )}
                              {subCount > 0 && (
                                <span className="text-[11px] text-gray-400">
                                  {subCount} klasör
                                </span>
                              )}
                              {fileCount > 0 && (
                                <span className="text-[11px] text-gray-400">
                                  {fileCount} dosya
                                </span>
                              )}
                            </div>
                          </div>

                          <ChevronRight
                            className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0"
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {filteredFiles.length > 0 && (
                <div className="space-y-1">
                  {filteredFolders.length > 0 && filteredFiles.length > 0 && (
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider px-1 mb-2 mt-4">
                      PDF Dosyaları
                    </p>
                  )}
                  {filteredFiles.map((file: any) => (
                    <button
                      key={file.id}
                      onClick={() => handleSelect(file)}
                      disabled={loadingDetails && selectedFile?.id === file.id}
                      className={`w-full text-left transition-all duration-200 rounded-xl ${selectedFile?.id === file.id
                        ? "ring-2 ring-indigo-500 bg-indigo-50"
                        : "hover:bg-gray-50 hover:-translate-y-0.5"
                        } disabled:cursor-wait`}
                    >
                      <div className="flex items-center gap-3 px-3 py-3 border border-gray-150 rounded-xl">
                        <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-red-500" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {file.originalName || file.name}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
                            <span>{formatFileSize(file.size)}</span>
                            <span>•</span>
                            <span>{formatDate(file.createdAt)}</span>
                          </div>
                        </div>

                        {loadingDetails && selectedFile?.id === file.id ? (
                          <Loader2 className="w-5 h-5 animate-spin text-indigo-600 flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <ChevronRight className="w-4 h-4 text-indigo-500" />
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 py-3 flex items-center justify-between">
          <p className="text-xs text-gray-400">
            {filteredFolders.length > 0 && `${filteredFolders.length} klasör`}
            {filteredFolders.length > 0 && filteredFiles.length > 0 && " · "}
            {filteredFiles.length > 0 && `${filteredFiles.length} dosya`}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            İptal
          </button>
        </div>
      </div>
    </Modal>
  );
}
